import { useState } from "react";
import { Trophy, ArrowUpRight, Timer } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHeader } from "../components/Bits";
import { Reveal } from "../components/Reveal";
import { EVENT, LINKS } from "../lib/site";

const DIVISIONS = [
  { key: "5K", label: "5 km", desc: "Chip-timed, age group medals" },
  { key: "1K", label: "Kids 1 km Dash", desc: "Ages 4–12" },
  { key: "__all__", label: "All finishers", desc: "Every trotter, every time" },
];

const resultsUrl = (division) => `${LINKS.results}&division=${division}`;

export default function Results() {
  const [division, setDivision] = useState("5K");

  return (
    <>
      <Seo title="Race Results" path="/results" description="Official chip-timed results from the Grimsby On The Lake Turkey Trot 5 km and Kids 1 km Dash, timed by Splits Timing." />
      <PageHeader
        chapter="the finish line"
        title="Official race results."
        sub={`The ${EVENT.dateLabel} ${EVENT.shortName} is in the books! Official chip-timed results for the 5 km and Kids 1 km Dash are live, courtesy of Splits Timing.`}
      />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24 sm:pb-32">
        <Reveal>
          <div className="flex flex-wrap gap-3" role="tablist" data-testid="results-tabs">
            {DIVISIONS.map((d) => {
              const active = d.key === division;
              return (
                <button
                  key={d.key}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setDivision(d.key)}
                  className={`group text-left rounded-2xl px-5 py-4 border transition-all duration-200 ${active ? "bg-terracotta text-white border-terracotta shadow-warm" : "bg-white border-black/5 hover:border-terracotta/40"}`}
                  data-testid={`results-tab-${d.key}`}
                >
                  <span className="flex items-center gap-2 font-display text-xl font-semibold">
                    {d.key === "__all__" ? <Trophy size={18} /> : <Timer size={18} />} {d.label}
                  </span>
                  <span className={`block mt-1 text-xs ${active ? "text-white/80" : "text-ink-soft"}`}>{d.desc}</span>
                </button>
              );
            })}
          </div>

          <div className="mt-6 relative rounded-3xl overflow-hidden shadow-warm-lg border border-black/5 bg-white h-[720px] sm:h-[860px]">
            <iframe
              key={division}
              className="absolute inset-0 h-full w-full"
              src={resultsUrl(division)}
              title={`Grimsby Turkey Trot ${division} results`}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="results-frame"
            />
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
            <p className="text-xs text-ink-soft">Official timing by Splits Timing. Trouble viewing? Open the results in a new tab.</p>
            <a href={resultsUrl(division)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-ink text-cream text-sm font-semibold hover:bg-terracotta transition-colors duration-200" data-testid="results-open-external">
              Open full results <ArrowUpRight size={16} />
            </a>
          </div>
        </Reveal>
      </section>
    </>
  );
}
